import { useMemo, useState } from "react";
import { format, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  FileText,
  Loader2,
  MessageSquareText,
  PenLine,
  Play,
  RotateCcw,
  Stethoscope,
  UserPlus,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

type Evento = {
  id: string;
  tipo: string;
  em: string | Date;
  titulo: string;
  detalhe: string | null;
  autor: string | null;
  appointmentId: string | null;
};

/** Ícone e cor por tipo — o que faz a linha do tempo ser escaneável sem ler cada linha. */
const META: Record<string, { icone: typeof ClipboardList; cor: string; fundo: string }> = {
  cadastro: { icone: UserPlus, cor: "#71717a", fundo: "#f4f4f5" },
  consulta: { icone: Stethoscope, cor: "#2457ff", fundo: "#eef2ff" },
  atendimento: { icone: Play, cor: "#0891b2", fundo: "#ecfeff" },
  observacao: { icone: MessageSquareText, cor: "#7c3aed", fundo: "#f5f3ff" },
  pedido: { icone: ClipboardList, cor: "#0ea5e9", fundo: "#f0f9ff" },
  retrabalho: { icone: RotateCcw, cor: "#f43f5e", fundo: "#fff1f2" },
  alteracao: { icone: PenLine, cor: "#f59e0b", fundo: "#fffbeb" },
  arquivo: { icone: FileText, cor: "#10b981", fundo: "#ecfdf5" },
  dados: { icone: UserPlus, cor: "#a1a1aa", fundo: "#fafafa" },
  plano: { icone: ClipboardCheck, cor: "#2563eb", fundo: "#eff6ff" },
  anamnese: { icone: ClipboardPen, cor: "#d97706", fundo: "#fffbeb" },
};

/** Evento que tem aba própria na ficha — o título vira atalho pra ela. */
const ABA_DO_TIPO: Record<string, string> = { plano: "tratamentos", anamnese: "anamnese" };

const FILTROS = [
  { chave: "tudo", rotulo: "Tudo" },
  { chave: "atendimento", rotulo: "Atendimentos" },
  { chave: "pedido", rotulo: "Prótese" },
  { chave: "plano", rotulo: "Planos" },
  { chave: "anamnese", rotulo: "Anamnese" },
  { chave: "arquivo", rotulo: "Arquivos" },
] as const;

/**
 * O que já aconteceu com o paciente, em ordem. Antes esta aba só sabia de pedidos de prótese —
 * agora reúne consultas, atendimentos, observações, retrabalhos, alterações, arquivos e edições
 * de cadastro, cada um respeitando o escopo do seu domínio (resolvido no servidor).
 *
 * Todo evento é um FATO que o sistema presenciou. A única linha com juízo clínico é a observação
 * da consulta, que continua vindo do dentista — o sistema nunca conclui nada pelo profissional.
 */
export function PatientHistoricoTab({ patientId, onIrParaAba }: { patientId: string; onIrParaAba?: (aba: string) => void }) {
  const [filtro, setFiltro] = useState<string>("tudo");
  const query = trpc.patient.timeline.useQuery({ patientId });

  const visiveis = useMemo(() => {
    const eventos = (query.data ?? []) as Evento[];
    if (filtro === "tudo") return eventos;
    if (filtro === "atendimento") return eventos.filter((e) => ["consulta", "atendimento", "observacao"].includes(e.tipo));
    if (filtro === "pedido") return eventos.filter((e) => ["pedido", "retrabalho", "alteracao"].includes(e.tipo));
    return eventos.filter((e) => e.tipo === filtro);
  }, [query.data, filtro]);

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Histórico do paciente</h3>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            Tudo que já aconteceu, mais recente primeiro. Você só vê o que o seu acesso alcança.
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTROS.map((f) => (
            <button
              key={f.chave}
              type="button"
              onClick={() => setFiltro(f.chave)}
              className={cn(
                "inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium transition-colors",
                filtro === f.chave
                  ? "bg-brand-600 font-semibold text-white"
                  : "text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted",
              )}
            >
              {f.rotulo}
            </button>
          ))}
        </div>
      </div>

      {visiveis.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/50 p-8 text-center dark:border-border dark:bg-muted/20">
          <ClipboardList className="mx-auto h-10 w-10 text-zinc-300 dark:text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-zinc-700 dark:text-foreground">
            {filtro === "tudo" ? "Nada registrado ainda" : "Nada desse tipo ainda"}
          </p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            Consultas, pedidos e arquivos aparecem aqui automaticamente.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col">
          {visiveis.map((e, i) => {
            const data = new Date(e.em);
            const anterior = i > 0 ? new Date(visiveis[i - 1].em) : null;
            const novoDia = !anterior || !isSameDay(data, anterior);
            const meta = META[e.tipo] ?? META.dados;
            const Icone = meta.icone;
            return (
              <li key={e.id}>
                {novoDia && (
                  <p className="pb-1.5 pt-4 text-[11px] font-bold uppercase tracking-wider text-zinc-400 first:pt-0 dark:text-muted-foreground/70">
                    {format(data, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                )}
                <div className="flex gap-3">
                  <div className="flex w-7 shrink-0 flex-col items-center">
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: meta.fundo }}
                    >
                      <Icone className="h-3.5 w-3.5" style={{ color: meta.cor }} />
                    </span>
                    {i < visiveis.length - 1 && <span className="w-px flex-1 bg-zinc-200 dark:bg-border" />}
                  </div>
                  <div className="min-w-0 flex-1 pb-4">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      {ABA_DO_TIPO[e.tipo] && onIrParaAba ? (
                        <button
                          type="button"
                          onClick={() => onIrParaAba(ABA_DO_TIPO[e.tipo])}
                          className="text-left text-sm font-semibold text-zinc-900 hover:text-brand-700 hover:underline dark:text-foreground"
                        >
                          {e.titulo}
                        </button>
                      ) : (
                        <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">{e.titulo}</p>
                      )}
                      <span className="text-[11px] tabular-nums text-zinc-400 dark:text-muted-foreground/70">
                        {format(data, "HH:mm")}
                      </span>
                      {e.autor && (
                        <span className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
                          · {e.autor}
                        </span>
                      )}
                    </div>
                    {e.detalhe && (
                      <p
                        className={cn(
                          "mt-0.5 whitespace-pre-wrap break-words text-[13px]",
                          e.tipo === "observacao"
                            ? "rounded-md bg-violet-50 px-2.5 py-1.5 text-zinc-700 dark:bg-violet-500/10 dark:text-foreground/80"
                            : "text-zinc-600 dark:text-muted-foreground",
                        )}
                      >
                        {e.detalhe}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
